// FOUNDER-WALK-LIVE-RECONCILE-025 · F1 — THE PARSER-FIRST FALLBACK STAMP, FOR EVERY SERVER LAYER.
//
// WHAT e12bc DID, AND WHAT IT LEFT. Commit e12bc ("parser-first") put an inline script ahead of the
// `.sg-export-head` block in `ExportShell` so the visitor's own browser stamps `data-sg-js` on <html>
// before the parser reaches the block, and a stylesheet rule hides the block only under that stamp.
// Measured there: JS off → the block is delivered visible (crawler-equivalence leg A holds); JS on →
// `display:none` from the very first paint, so the H1 and tagline no longer flash before the design
// mounts. That was exactly right and it covered exactly ONE of the three server layers the design
// paints over:
//
//     ExportShell            .sg-export-head[data-sg-server-layer]   ← e12bc: stamped, hidden
//     EXPORT_EXEMPLAR branch [data-sg-server-layer] body blocks       ← NOT stamped: text flashed
//     mountain-root branch   [data-sg-server-layer] MountainLanding   ← NOT stamped: whole old page flashed
//
// The second and third are the "exported text / old page flash" the founder keeps seeing on
// /mountain-whistler and its subpages at 1440 and 390: the server-rendered fallback (a full
// mirror-styled landing on the root, the extraction's paragraphs and images on the subpages) painted
// at ~2 s and stayed until the design proved it painted at 5–19 s. Same defect, same cure, applied at
// the SAME point in the parse for every layer that has a design mounting over it.
//
// ── THE THREE FALLBACKS THIS MUST NOT BREAK, and how each is kept ───────────────────────────────
//
//   1. JS DISABLED (crawler-equivalence leg A's context, and any crawler that does not execute JS):
//      the inline script never runs, the stamp never lands, every layer is delivered visible, real
//      box, no clip. Nothing here is server-emitted `display:none`.
//   (4. — added in 025-R1 — STALLED BOOT: a script DID run and wrote "booting", but the design never
//      reaches "up" because a dependency never arrives. See SERVER_LAYER_STALL_MS below: revealed at
//      a clock above the measured legitimate boot, never before it.)
//   2. SCRIPT FAILURE (the route chunk never executes — a 404'd chunk, a CSP refusal, a fatal error at
//      module evaluation): the stamp DID land, so without a second signal the page would stay blank
//      forever. e12bc had this hole. The script therefore watches for the one fact only a booted app
//      can write — `window.__SG_DESIGN_STATE` — and if it is still absent after the document's `load`
//      event plus a grace, or by a hard ceiling in case `load` never fires, it REMOVES the stamp and
//      records why on <html>. A design that DID boot and then failed (`state === "failed"`) is
//      DesignSurface's job, unchanged: its failed/threw paths write an INLINE `display:block` on every
//      layer, which outranks this stylesheet rule.
//   3. DESIGN UP: DesignSurface still writes inline `display:none` + aria-hidden + inert on the layer
//      once it has PROVEN a paint (`state === "up"`). The stamp merely covers the seconds before.
//
// WHY A STYLESHEET RULE UNDER A STAMP AND NOT AN INLINE STYLE: an inline `display:none` on the layer
// would be in the DELIVERED bytes, which is the P0 2026-08-24 class the crawler-equivalence gate
// exists to refuse. The stamp is written by the visitor's browser, never by the server.
//
// WHY THE CHECK RE-VERIFIES BEFORE REVEALING: a cancelled boot (`recoverCancelledBoot`) deletes the
// state for one render before the retry writes "booting" again. A check landing in that gap would
// reveal a layer a boot is about to hide; a 150 ms second look closes the gap without changing the
// verdict for a chunk that genuinely never ran.
//
// Server component, deliberately: no "use client", no hooks, no listener registered by React. The
// script is executed by the HTML parser exactly once, in document order, before the layer it guards.
//
// IMPL-008 (run 2026-10-02) — `pending()` NOW READS THE PARSER-FIRST LIVENESS MARKER TOO. The
// no-boot reveals below exist for a route chunk that NEVER RAN (hole 2 in the ledger above). But
// EarlyPaintReveal is a script that runs parser-first and lawfully holds `__SG_DESIGN_STATE = "up"`
// behind hydration (its osm-map #418 gate), so on a slow transport the state is still undefined at
// load+2000ms while the SSR design is already correct and visible — and this file's own check then
// uncovered the legacy fallback layer OVER it (the design-script-readiness.ts KNOWN LIMIT, measured).
// EarlyPaintReveal therefore publishes EARLY_BOOT_LIVENESS_PROP before it waits on anything, and
// `pending()` treats that marker as the proof-of-life it is: "a script ran" is exactly the question
// the no-boot paths ask. The 20s stalled-boot reveal asks a DIFFERENT question — "did the boot ever
// finish" — and keeps reading only the state, so an alive-but-never-up boot (failed dependency AND
// a dead React chunk) is still bounded at SERVER_LAYER_STALL_MS, never hidden forever.

import { EARLY_BOOT_LIVENESS_PROP } from "./design-script-readiness";

export const SERVER_LAYER_STAMP_ATTR = "data-sg-js";
export const SERVER_LAYER_REVEALED_ATTR = "data-sg-js-revealed";
/** How long after `load` a missing boot state means "no script will run" — see the file header. */
export const SERVER_LAYER_GRACE_MS = 2000;
/** The hard ceiling for the same decision when `load` never fires (a hung subresource). */
export const SERVER_LAYER_CEILING_MS = 12000;
/**
 * 025-R1 — THE STALLED-BOOT CEILING. `pending()` below answers only "did any script ever write a
 * boot state"; it is blind to a boot that WROTE "booting" and then never reached "up" — measured by
 * the independent verifier on the 3028 candidate with the design's vendor bundle
 * (/design/vendor/react.production.min.js) delayed 20 s: at 14 s the state read "booting", the stamp
 * was on <html>, zero template nodes had rendered and the server layer was display:none — a blank
 * page with a perfectly good fallback hidden under it. DesignSurface's own recovery starts only AFTER
 * the runtime hands back a verdict, so a dependency that never arrives is a hole between the two.
 *
 * So: if the design has not reached "up" by this clock, the stamp comes off and the fallback paints
 * (`data-sg-js-revealed="stalled-boot"`). A design that comes up LATER still hides the layer, because
 * DesignSurface writes inline `display:none` on every [data-sg-server-layer] at "up" and inline wins
 * over the stamp's stylesheet rule — the same lifecycle as before this component existed.
 *
 * WHY 20 s AND NOT THE 12 s CEILING ABOVE: the 023 walk measured a LEGITIMATE design-up on staging at
 * 5.8 s (1440) and 16–19 s (390). A stall clock inside that window would repaint the founder's
 * finding-8 flash on every slow phone load (old page at 12 s, design at 17 s). Above it, a genuine
 * stall is bounded at 20 s instead of forever, and a slow-but-live boot is left alone. The speed
 * itself is finding 8's other half and is not changed here.
 */
export const SERVER_LAYER_STALL_MS = 20000;

/** Hides every server layer — and e12bc's head block — ONLY under the visitor-written stamp. */
export const SERVER_LAYER_PARSER_FIRST_CSS =
  `html[${SERVER_LAYER_STAMP_ATTR}] .sg-export-head{display:none}` +
  `html[${SERVER_LAYER_STAMP_ATTR}] [data-sg-server-layer]{display:none}`;

/** The inline script. ES5 on purpose: it runs before any bundle, in whatever engine asked. */
export const SERVER_LAYER_STAMP_SCRIPT =
  "(function(){" +
  `var d=document.documentElement;d.setAttribute("${SERVER_LAYER_STAMP_ATTR}","");` +
  "var done=false;" +
  `function reveal(why){if(done)return;done=true;d.removeAttribute("${SERVER_LAYER_STAMP_ATTR}");d.setAttribute("${SERVER_LAYER_REVEALED_ATTR}",why);}` +
  // IMPL-008: no state AND no liveness marker — only then has no script provably run (see header).
  `function pending(){var s=window.__SG_DESIGN_STATE;if(s!==undefined&&s!==null)return false;return window.${EARLY_BOOT_LIVENESS_PROP}===undefined;}` +
  "function check(why){if(!pending())return;setTimeout(function(){if(pending())reveal(why);},150);}" +
  `window.addEventListener("load",function(){setTimeout(function(){check("no-boot-after-load");},${SERVER_LAYER_GRACE_MS});});` +
  `setTimeout(function(){check("no-boot-by-ceiling");},${SERVER_LAYER_CEILING_MS});` +
  // 025-R1 — the stalled-boot ceiling (see SERVER_LAYER_STALL_MS): not "up" by the clock, re-verified
  // 150 ms later, reveals. "failed" is already restored inline by DesignSurface, so revealing on it
  // changes nothing on screen; "up" is the only state that keeps the layer hidden.
  `function stalled(){return window.__SG_DESIGN_STATE!=="up";}` +
  `setTimeout(function(){if(!stalled())return;setTimeout(function(){if(stalled())reveal("stalled-boot");},150);},${SERVER_LAYER_STALL_MS});` +
  "})();";

/**
 * Mount ONCE per design-bearing branch, in document order BEFORE the first `[data-sg-server-layer]`
 * that branch emits. Emitting it twice is harmless (the stamp is idempotent) but pointless.
 */
export default function ServerLayerParserFirst({ title: _title }: { title?: string }) {
  return (
    <>
      <style data-sg-parser-first="">{SERVER_LAYER_PARSER_FIRST_CSS}</style>
      <script data-sg-parser-first="" dangerouslySetInnerHTML={{ __html: SERVER_LAYER_STAMP_SCRIPT }} />
    </>
  );
}
